import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import {
	EnvelopeIcon,
	PhoneIcon,
	MapPinIcon,
	GlobeAltIcon,
	BriefcaseIcon,
	PlusIcon,
	ChevronRightIcon,
	WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

import { getContact, deleteContact } from '../../api/contacts';
import { getAssets, addAssetContacts, removeAssetContact } from '../../api/assets';
import { getContracts, updateContract } from '../../api/contracts';
import { getWorkOrders } from '../../api/workOrders';
import { getUserFullName } from '../../components/helpers/functions';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import { StackableModal, AssetSelectorBeta } from '../../components/nextgen';
import { StatusBadge } from '../../components/nextgen/Badge';
import { AvatarGenerated } from '../../components/Avatar';
import Button from '../../components/Button';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import EllipsisDropdown, {
	EllipsisDropdownItem,
} from '../../components/EllipsisDropdown';
import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSettings from '../../hooks/useSettings';
import { parseAssetTemplate } from '../../components/nextgen/AssetSelectorBeta';

const VENDOR_TYPES = {
	manufacturer: 'Manufacturer',
	service_vendor: 'Service Vendor',
	distributor: 'Distributor',
	other: 'Other',
};

export default () => {
	const navigate = useNavigate();
	const { id } = useParams();

	const {
		value: contact,
		execute: refreshContact,
		status,
	} = useAsync(getContact, { immediate: false });

	const { execute: onDeleteContact } = useAsync(deleteContact, {
		immediate: false,
	});

	const { value: assetsData } = useAsync(getAssets, {
		defaultPayload: { pageSize: null },
	});

	const { value: contractsData } = useAsync(getContracts, {
		defaultPayload: { pageSize: null },
	});

	const {
		value: workOrdersData,
		execute: refreshWorkOrders,
	} = useAsync(getWorkOrders, { immediate: false });

	const { execute: onAddAssetContact, status: addingAsset } = useAsync(
		addAssetContacts,
		{
			immediate: false,
			showNotification: { success: 'Asset assigned successfully' },
		},
	);

	const { execute: onRemoveAssetContact } = useAsync(removeAssetContact, {
		immediate: false,
		showNotification: { success: 'Asset removed successfully' },
	});

	const { execute: onUpdateContract, status: addingContract } = useAsync(
		updateContract,
		{
			immediate: false,
			showNotification: { success: 'Contract updated successfully' },
		},
	);

	const { setOpen: setDeleteModalOpen, setClose: setDeleteModalClose } =
		useModal('deleteModal');

	const { setOpen: setContactModalOpen } = useModal('contactModal');
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');
	const { setOpen: openWorkOrdersList } = useModal('workOrdersList');

	const settings = useSettings();
	const assetDisplayTemplate = settings.get('assetDisplay');

	// Modal states
	const [addAssetModalOpen, setAddAssetModalOpen] = useState(false);
	const [selectedAssetId, setSelectedAssetId] = useState(null);
	const [addContractModalOpen, setAddContractModalOpen] = useState(false);
	const [selectedContractId, setSelectedContractId] = useState(null);

	useEffect(() => {
		if (id) {
			refreshContact({ id });
			refreshWorkOrders({ vendorId: id, pageSize: 100 });
		}
	}, [id, refreshContact, refreshWorkOrders]);

	// For companies, combine company assets + employee assets (deduplicated)
	const allAssets = useMemo(() => {
		if (!contact) return [];

		if (contact.type === 'company') {
			const assetMap = new Map();

			// Add company's direct assets
			(contact.assets || []).forEach((asset) => {
				assetMap.set(asset.id, { ...asset, source: 'company' });
			});

			// Add employees' assets
			(contact.contacts || []).forEach((employee) => {
				(employee.assets || []).forEach((asset) => {
					if (!assetMap.has(asset.id)) {
						assetMap.set(asset.id, { ...asset, source: 'employee', employeeName: getUserFullName(employee) });
					}
				});
			});

			return Array.from(assetMap.values());
		}

		return contact.assets || [];
	}, [contact]);

	// Filter available assets (not already assigned)
	const availableAssets = useMemo(() => {
		if (!assetsData?.rows) return [];
		const assignedAssetIds = allAssets.map((a) => a.id);
		return assetsData.rows
			.filter((asset) => !assignedAssetIds.includes(asset.id))
			.map((asset) => ({
				id: asset.id,
				name:
					[
						asset.manufacturer?.name || asset.manufacturerName,
						asset.model?.name || asset.modelName,
					]
						.filter(Boolean)
						.join(' ') || 'Unnamed Asset',
				manufacturer:
					asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
				location: asset.location?.name || 'No location',
				serial: asset.serialNumber || 'N/A',
				serialNumber: asset.serialNumber,
			}));
	}, [assetsData, allAssets]);

	// Filter available contracts (not already assigned)
	const availableContracts = useMemo(() => {
		if (!contractsData?.rows) return [];
		const assignedContractIds = (contact?.contracts || []).map((c) => c.id);
		return contractsData.rows.filter(
			(contract) => !assignedContractIds.includes(contract.id),
		);
	}, [contractsData, contact?.contracts]);

	const handleAddAsset = () => {
		if (!selectedAssetId) return;
		onAddAssetContact(
			{
				id: selectedAssetId,
				contacts: [contact.id],
			},
			() => {
				setAddAssetModalOpen(false);
				setSelectedAssetId(null);
				setTimeout(() => refreshContact({ id }), 100);
			},
		);
	};

	const handleRemoveAsset = (assetId) => {
		onRemoveAssetContact(
			{
				id: assetId,
				contacts: [contact.id],
			},
			() => {
				setDeleteModalClose();
				setTimeout(() => refreshContact({ id }), 100);
			},
		);
	};

	const handleAddContract = () => {
		if (!selectedContractId) return;
		onUpdateContract(
			{
				id: selectedContractId,
				vendor: contact.id,
			},
			() => {
				setAddContractModalOpen(false);
				setSelectedContractId(null);
				setTimeout(() => refreshContact({ id }), 100);
			},
		);
	};

	const handleRemoveContract = (contractId) => {
		onUpdateContract(
			{
				id: contractId,
				vendor: null,
			},
			() => {
				setDeleteModalClose();
				setTimeout(() => refreshContact({ id }), 100);
			},
		);
	};

	// Get display name
	const getDisplayName = () => {
		if (!contact) return '';
		return contact.type === 'company'
			? contact.companyName || 'Unnamed Company'
			: getUserFullName(contact) || 'Unnamed Contact';
	};

	// Get vendor type label
	const getVendorType = () => {
		if (!contact) return '';
		if (contact.vendorType && VENDOR_TYPES[contact.vendorType]) {
			return VENDOR_TYPES[contact.vendorType];
		}
		return contact.type === 'company' ? 'Company' : 'Individual';
	};

	return (
		<MainContent maxWidth="w-full bg-gray-50 min-h-screen">
			{status === 'pending' && !contact ? (
				<div className="max-w-5xl mx-auto">
					<LoadingSkeleton />
				</div>
			) : !contact ? (
				<div className="text-center py-12">
					<p className="text-sm text-gray-500">Contact not found</p>
				</div>
			) : (
				<div className="max-w-5xl mx-auto">
					{/* Header */}
					<PageHeading
						RightElement={
							<div className="flex items-center gap-x-2">
								<Button
									type="secondary"
									onClick={() => {
										setContactModalOpen({
											id,
											onSuccess: () => refreshContact({ id }),
										});
									}}
								>
									Edit
								</Button>
								<EllipsisDropdown>
									<EllipsisDropdownItem
										danger
										onClick={() =>
											setDeleteModalOpen({
												title: 'Are you sure you want to delete this contact?',
												description:
													'This action cannot be undone. All data associated with this contact will be permanently removed.',
												confirmText: 'Delete Contact',
												onConfirm: () =>
													onDeleteContact(contact?.id, () => {
														setDeleteModalClose();
														setTimeout(() => {
															navigate('/manage/directory');
														}, 0);
													}),
											})
										}
									>
										Delete contact
									</EllipsisDropdownItem>
								</EllipsisDropdown>
							</div>
						}
						divider={false}
					>
						<button
							onClick={() => navigate('/manage/directory')}
							className="text-sm text-gray-500 hover:text-gray-700 font-normal"
						>
							← Back to Directory
						</button>
					</PageHeading>

					<div className="">
						<div className="grid grid-cols-5 gap-x-6">
							<div className="col-span-2">
								<div className="bg-white border-gray-200 border rounded-lg mt-6">
									<div className="flex items-center gap-3 py-4 px-6 border-b border-gray-200">
										{contact.type === 'person' ? (
											<AvatarGenerated
												size="sm"
												id={getUserFullName(contact)}
											/>
										) : (
											<AvatarGenerated size="sm" id={contact.companyName} />
										)}
										<h3 className="text-sm font-medium text-gray-900">
											{getDisplayName()}
										</h3>
									</div>

									<dl className="grid grid-cols-1 gap-y-4 px-6 pb-6">
										{contact.type === 'person' && (
											<div className="flex items-start gap-3">
												<BriefcaseIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
												<div className="min-w-0 flex-1">
													<dt className="text-xs font-medium text-gray-500">
														Title
													</dt>
													<dd className="mt-1 text-sm text-gray-900 break-words">
														{contact.title ? (
															contact.title
														) : (
															<span className="text-gray-400">—</span>
														)}
													</dd>
												</div>
											</div>
										)}

										<div className="flex items-start gap-3">
											<EnvelopeIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Email
												</dt>
												<dd className="mt-1 text-sm text-gray-900 break-words">
													{contact.email ? (
														<a
															href={`mailto:${contact.email}`}
															className="text-indigo-600 hover:text-indigo-700"
														>
															{contact.email}
														</a>
													) : (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<PhoneIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Phone
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{contact.phoneNumber ? (
														<a
															href={`tel:${contact.phoneNumber}`}
															className="text-indigo-600 hover:text-indigo-700"
														>
															{contact.phoneNumber}
														</a>
													) : (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<MapPinIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Location
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{contact.location || (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<GlobeAltIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Website
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{contact.url ? (
														<a
															href={contact.url}
															target="_blank"
															rel="noopener noreferrer"
															className="text-indigo-600 hover:text-indigo-700"
														>
															{contact.url}
														</a>
													) : (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>
									</dl>
								</div>

								{/* Company section for persons */}
								{contact.type === 'person' && contact.company && (
									<div className="bg-white border-gray-200 border rounded-lg mt-6">
										<div className="py-4 px-6 border-b border-gray-200">
											<h3 className="text-sm font-medium text-gray-900 flex items-center">
												Company
											</h3>
										</div>
										<div
											className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
											onClick={() =>
												navigate(`/manage/directory/${contact.company.id}`)
											}
										>
											<div className="flex items-center gap-3">
												<AvatarGenerated
													size="sm"
													id={contact.company.companyName}
												/>
												<div className="min-w-0 flex-1">
													<p className="text-sm font-medium text-gray-900">
														{contact.company.companyName}
													</p>
												</div>
											</div>
										</div>
									</div>
								)}

								{/* Employees section for companies */}
								{contact.type === 'company' && (
									<div className="bg-white border-gray-200 border rounded-lg mt-6">
										<div className="py-4 px-6 border-b border-gray-200">
											<h3 className="text-sm font-medium text-gray-900 flex items-center">
												Employees{' '}
												{contact.contacts &&
													contact.contacts.length > 0 &&
													`(${contact.contacts.length})`}
											</h3>
										</div>
										{contact.contacts && contact.contacts.length > 0 ? (
											<ul className="divide-y divide-gray-200  m-0 p-0 !mt-0">
												{contact.contacts.map((person) => (
													<li
														key={person.id}
														className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
														onClick={() =>
															navigate(`/manage/directory/${person.id}`)
														}
													>
														<div className="flex items-center gap-3">
															<AvatarGenerated
																size="sm"
																id={getUserFullName(person)}
															/>
															<div className="min-w-0 flex-1">
																<p className="text-sm font-medium text-gray-900">
																	{getUserFullName(person)}
																</p>
																{person.title && (
																	<p className="text-xs text-gray-500">
																		{person.title}
																	</p>
																)}
															</div>
															<div className="flex items-center gap-2 text-xs text-gray-500">
																{person.email && (
																	<a
																		href={`mailto:${person.email}`}
																		className="text-indigo-600 hover:text-indigo-700"
																		onClick={(e) => e.stopPropagation()}
																	>
																		<EnvelopeIcon className="w-4 h-4" />
																	</a>
																)}
																{person.phoneNumber && (
																	<a
																		href={`tel:${person.phoneNumber}`}
																		className="text-indigo-600 hover:text-indigo-700"
																		onClick={(e) => e.stopPropagation()}
																	>
																		<PhoneIcon className="w-4 h-4" />
																	</a>
																)}
															</div>
														</div>
													</li>
												))}
											</ul>
										) : (
											<div className="px-6 py-8 text-center">
												<p className="text-sm text-gray-500">No employees</p>
											</div>
										)}
									</div>
								)}
							</div>
							<div className="col-span-3">
								{/* Assets */}
								<div className="bg-white rounded-lg border border-gray-200 mt-6">
									<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
										<h3 className="text-sm font-medium text-gray-900 flex items-center">
											Assets{' '}
											{allAssets.length > 0 && `(${allAssets.length})`}
										</h3>
										<Button
											type="secondary"
											size="sm"
											Icon={PlusIcon}
											onClick={() => setAddAssetModalOpen(true)}
										>
											Add Asset
										</Button>
									</div>
									{allAssets.length > 0 ? (
										<ul className="divide-y divide-gray-200  m-0 p-0 !mt-0">
											{allAssets.map((asset) => (
												<li
													key={asset.id}
													className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
													onClick={() =>
														navigate(`/assets/${asset.id}/overview`)
													}
												>
													<div className="flex items-center justify-between">
														<div className="min-w-0 flex-1">
															<p className="text-sm font-medium text-gray-900 truncate">
																{[
																	asset.manufacturer?.name ||
																		asset.manufacturerName,
																	asset.model?.name || asset.modelName,
																]
																	.filter(Boolean)
																	.join(' ') || 'Unnamed Asset'}
															</p>
															<p className="text-xs text-gray-500 truncate">
																{assetDisplayTemplate
																	? parseAssetTemplate(
																			assetDisplayTemplate,
																			asset,
																		)
																	: `${asset.location?.name || 'No location'} · SN: ${asset.serialNumber || 'N/A'}`}
																{asset.source === 'employee' && (
																	<span className="ml-2 text-indigo-600">
																		(via {asset.employeeName})
																	</span>
																)}
															</p>
														</div>
														<div onClick={(e) => e.stopPropagation()}>
															<EllipsisDropdown>
																<EllipsisDropdownItem
																	danger
																	onClick={() =>
																		setDeleteModalOpen({
																			title: 'Remove asset from contact?',
																			description:
																				'This contact will no longer be associated with this asset.',
																			confirmText: 'Remove Asset',
																			onConfirm: () =>
																				handleRemoveAsset(asset.id),
																		})
																	}
																>
																	Remove asset
																</EllipsisDropdownItem>
															</EllipsisDropdown>
														</div>
													</div>
												</li>
											))}
										</ul>
									) : (
										<div className="px-6 py-8 text-center">
											<p className="text-sm text-gray-500">No assets</p>
										</div>
									)}
								</div>

								{/* Work Orders Section */}
								<div className="bg-white border-gray-200 border rounded-lg mt-6">
									<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
										<h3 className="text-sm font-medium text-gray-900 flex items-center">
											Active Work Orders
										</h3>
										<div className="flex items-center space-x-2">
											<Button
												type="ghost"
												size="sm"
												onClick={() =>
													openWorkOrdersList({
														contactId: id,
														contactName: contact?.companyName || getUserFullName(contact) || 'Contact',
													})
												}
											>
												View All
											</Button>
										</div>
									</div>
									{workOrdersData?.rows?.filter(
										(wo) => !['completed', 'cancelled'].includes(wo.status),
									)?.length > 0 ? (
										<ul className="divide-y divide-gray-100 rounded-md m-0 p-0 !mt-0">
											{workOrdersData?.rows
												?.filter(
													(wo) => !['completed', 'cancelled'].includes(wo.status),
												)
												?.slice(0, 5)
												.map((workOrder) => (
													<li
														key={workOrder.id}
														className="flex items-center justify-between py-3 px-6 text-xs hover:bg-gray-50 cursor-pointer"
														onClick={() => openWorkOrderOverview({ id: workOrder?.id })}
													>
														<div className="flex items-center min-w-0 flex-1">
															<div className="min-w-0 flex-1">
																<p className="truncate font-medium text-gray-900">
																	{workOrder?.title || 'Untitled'}
																</p>
																<p className="text-xs text-gray-500">
																	{workOrder?.dueDate && (
																		<span>
																			Due{' '}
																			{moment(workOrder.dueDate).format(
																				'MMM D, YYYY',
																			)}
																		</span>
																	)}
																</p>
															</div>
														</div>
														<div className="flex items-center">
															<StatusBadge
																size="xs"
																status={workOrder?.status}
																className="mr-3"
															/>
															<ChevronRightIcon className="h-4 w-4 text-gray-400" />
														</div>
													</li>
												))}
										</ul>
									) : (
										<div className="text-center py-6 rounded-md">
											<WrenchScrewdriverIcon className="mx-auto h-8 w-8 text-gray-300" />
											<p className="py-2 text-sm text-gray-500">
												No active work orders
											</p>
										</div>
									)}
								</div>

								<div className="bg-white border-gray-200 border rounded-lg mt-6">
									<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
										<h3 className="text-sm font-medium text-gray-900 flex items-center">
											Contracts{' '}
											{contact.contracts &&
												contact.contracts.length > 0 &&
												`(${contact.contracts.length})`}
										</h3>
										<Button
											type="secondary"
											size="sm"
											Icon={PlusIcon}
											onClick={() => setAddContractModalOpen(true)}
										>
											Add Contract
										</Button>
									</div>
									{contact.contracts && contact.contracts.length > 0 ? (
										<ul className="divide-y divide-gray-200 m-0 p-0 !mt-0">
											{contact.contracts.map((contract) => (
												<li
													key={contract.id}
													className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
													onClick={() =>
														navigate(`/manage/contracts/${contract.id}`)
													}
												>
													<div className="flex items-center justify-between">
														<div className="min-w-0 flex-1">
															<p className="text-sm font-medium text-gray-900 truncate">
																{contract.name || 'Unnamed Contract'}
															</p>
														</div>
														<div onClick={(e) => e.stopPropagation()}>
															<EllipsisDropdown>
																<EllipsisDropdownItem
																	danger
																	onClick={() =>
																		setDeleteModalOpen({
																			title: 'Remove contract from contact?',
																			description:
																				'This contact will no longer be the vendor for this contract.',
																			confirmText: 'Remove Contract',
																			onConfirm: () =>
																				handleRemoveContract(contract.id),
																		})
																	}
																>
																	Remove contract
																</EllipsisDropdownItem>
															</EllipsisDropdown>
														</div>
													</div>
												</li>
											))}
										</ul>
									) : (
										<div className="px-6 py-8 text-center">
											<p className="text-sm text-gray-500">No contracts</p>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Add Asset Modal */}
			<StackableModal
				open={addAssetModalOpen}
				onClose={() => {
					setAddAssetModalOpen(false);
					setSelectedAssetId(null);
				}}
				title="Add Asset"
				size="md"
			>
				<div className="space-y-4">
					<p className="text-sm text-gray-500 mb-3">
						Select an asset to assign to this contact.
					</p>
					<AssetSelectorBeta
						value={selectedAssetId}
						onChange={setSelectedAssetId}
						assets={availableAssets}
						placeholder="Search and select an asset..."
						multiple={false}
						disablePortal
						className="mt-3"
					/>
					<div className="flex justify-end gap-3 pt-4">
						<Button
							type="secondary"
							onClick={() => {
								setAddAssetModalOpen(false);
								setSelectedAssetId(null);
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							onClick={handleAddAsset}
							disabled={!selectedAssetId || addingAsset === 'pending'}
						>
							{addingAsset === 'pending' ? 'Adding...' : 'Add Asset'}
						</Button>
					</div>
				</div>
			</StackableModal>

			{/* Add Contract Modal */}
			<StackableModal
				open={addContractModalOpen}
				onClose={() => {
					setAddContractModalOpen(false);
					setSelectedContractId(null);
				}}
				title="Add Contract"
				size="md"
			>
				<div className="space-y-4">
					<p className="text-sm text-gray-500 mb-3">
						Select a contract to assign this contact as the vendor.
					</p>
					<select
						className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
						value={selectedContractId || ''}
						onChange={(e) => setSelectedContractId(e.target.value || null)}
					>
						<option value="">Select a contract...</option>
						{availableContracts.map((contract) => (
							<option key={contract.id} value={contract.id}>
								{contract.name || 'Unnamed Contract'}
							</option>
						))}
					</select>
					<div className="flex justify-end gap-3 pt-4">
						<Button
							type="secondary"
							onClick={() => {
								setAddContractModalOpen(false);
								setSelectedContractId(null);
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							onClick={handleAddContract}
							disabled={!selectedContractId || addingContract === 'pending'}
						>
							{addingContract === 'pending' ? 'Adding...' : 'Add Contract'}
						</Button>
					</div>
				</div>
			</StackableModal>
		</MainContent>
	);
};
